import { useState, useMemo } from "react";
import { ArrowUpRight, Clock, Euro } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "./Reveal";

const WEEKS = 46;
const AUTOMATION = 0.6;

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function EfficiencyCalculator() {
  const { lang, t } = useLang();
  const [team, setTeam] = useState(8);
  const [hours, setHours] = useState(10);
  const [cost, setCost] = useState(65);

  const { hoursSaved, moneySaved } = useMemo(() => {
    const h = Math.round(team * hours * WEEKS * AUTOMATION);
    return { hoursSaved: h, moneySaved: h * cost };
  }, [team, hours, cost]);

  const locale = lang === "nl" ? "nl-NL" : "en-US";
  const fmt = (n) => n.toLocaleString(locale);
  const fmtEur = (n) =>
    n.toLocaleString(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

  const sliders = [
    { key: "team", label: t.calc.team, value: team, set: setTeam, min: 1, max: 100, suffix: "" },
    { key: "hours", label: t.calc.hours, value: hours, set: setHours, min: 1, max: 30, suffix: "u" },
    { key: "cost", label: t.calc.cost, value: cost, set: setCost, min: 20, max: 150, suffix: "€" },
  ];

  return (
    <section className="relative py-28 md:py-36 bg-bg-2 border-y border-line overflow-hidden">
      <div className="pointer-events-none absolute -right-20 top-10 h-[420px] w-[420px] rounded-full bg-brand/10 blur-[120px]" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <Reveal><Overline>{t.calc.overline}</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-head font-bold tracking-tighter text-ink text-[clamp(2.25rem,5vw,4.5rem)] leading-[0.95] mt-5">
              {t.calc.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base md:text-lg text-ink-2 leading-relaxed mt-6">{t.calc.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-stretch">
          {/* Controls */}
          <Reveal className="rounded-2xl border border-line bg-bg p-8 md:p-10" data-testid="calculator">
            <div className="space-y-10">
              {sliders.map((s) => (
                <div key={s.key}>
                  <div className="flex items-baseline justify-between mb-4">
                    <label className="text-sm text-ink-2">{s.label}</label>
                    <span className="font-head font-bold text-ink text-2xl tabular-nums">
                      {s.suffix === "€" ? `€${s.value}` : `${s.value}${s.suffix}`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={s.min}
                    max={s.max}
                    value={s.value}
                    onChange={(e) => s.set(Number(e.target.value))}
                    className="range-brand"
                    data-testid={`calc-${s.key}`}
                  />
                </div>
              ))}
            </div>
          </Reveal>

          {/* Result */}
          <Reveal delay={0.1} className="rounded-2xl border border-brand/40 bg-brand p-8 md:p-10 flex flex-col justify-between text-white">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <Clock className="h-6 w-6 opacity-80" />
                <div className="font-head font-black tracking-tighter text-[clamp(2.5rem,5vw,4rem)] leading-none mt-5 tabular-nums" data-testid="calc-hours-result">
                  {fmt(hoursSaved)}
                </div>
                <div className="text-sm text-white/80 mt-2">{t.calc.resHours}</div>
              </div>
              <div>
                <Euro className="h-6 w-6 opacity-80" />
                <div className="font-head font-black tracking-tighter text-[clamp(2.5rem,5vw,4rem)] leading-none mt-5 tabular-nums" data-testid="calc-money-result">
                  {fmtEur(moneySaved)}
                </div>
                <div className="text-sm text-white/80 mt-2">{t.calc.resMoney}</div>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-white/20">
              <p className="text-xs text-white/70 mb-6">{t.calc.note}</p>
              <button
                onClick={() => scrollTo("contact")}
                className="group inline-flex items-center gap-2 bg-white text-brand font-medium rounded-full px-7 py-3.5 hover:bg-bg-2 transition-colors duration-300 active:scale-95"
                data-testid="calc-cta"
              >
                {t.calc.cta}
                <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
